// VOID Card Warfare — Account Fullscreen Modal
//
// Fullscreen dialog for Commander Profile review and Account Recovery input.
// Styled with background.webp and Startscreen + Arsenal > Progress sci-fi aesthetics.

import { getAuth, getDisplayName, getMMR } from '../net/playerIdentity.js';
import { showCallsignModal } from './callsignModal.js';
import { checkAlphaAccess } from './alphaGate.js';
import { getRankTier } from './leaderboardModal.js';
import { getCardThumbUrl } from '../data/cardThumbUrl.js';
import { getCardImageUrl, CARDS_DATA } from '../data/cards.js';
import { inventory } from '../engine/inventory.js';
import { progress } from '../engine/progress.js';
import { resolveAvatarImageUrl } from './avatarImage.js';
import { showRecoverPrompt, closeRecoverPrompt } from './accountOverlay.js';
import { showDataFeedModal, showRewardToast } from './dataFeedModal.js';
import { inboxManager } from '../net/inboxManager.js';

let activeOverlay = null;

export function showAccountFullscreenModal(game) {
  if (activeOverlay) {
    closeRecoverPrompt();
    activeOverlay.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
  }
  game?.overlayGate?.acquire?.();

  const audio = game?.audio;
  const auth = getAuth();
  const hasAccount = !!(auth && auth.accountId);
  const displayName = getDisplayName() || 'Commander';
  const mmr = getMMR();
  const tier = getRankTier(mmr);

  const equippedArtwork = inventory.getEquippedProfileArtwork();
  let avatarUrl = null;
  if (equippedArtwork) {
    const card = CARDS_DATA.find(c => c.name === equippedArtwork);
    if (card?.images?.[0]) {
      avatarUrl = getCardThumbUrl(card.images[0], 'm');
    }
  }
  if (!avatarUrl) {
    const equippedEntry = progress.getEquippedAvatar();
    avatarUrl = resolveAvatarImageUrl(equippedEntry);
  }
  if (!avatarUrl) {
    avatarUrl = getCardThumbUrl('1_Hypermarine.png', 'm');
  }

  const layer = document.createElement('div');
  layer.id = 'account-fullscreen-overlay';
  layer.className = 'fullscreen-dialog-overlay active';

  layer.innerHTML = `
    <div class="fullscreen-dialog-bg-layer" aria-hidden="true">
      <div class="fullscreen-dialog-bg-image"></div>
      <div class="fullscreen-dialog-scrim"></div>
      <div class="fullscreen-dialog-vignette"></div>
    </div>

    <div class="fullscreen-dialog-container">
      <!-- Top Title & Navigation Bar -->
      <header class="fullscreen-dialog-header">
        <div class="fullscreen-dialog-header-left">
          <div class="fullscreen-dialog-category">OPERATIVE · IDENTITY</div>
          <h2 class="fullscreen-dialog-title">PROFILE</h2>
        </div>
        <div class="fullscreen-dialog-header-actions">
          <div class="fullscreen-dialog-status-pill status-pill-circular" title="Commander Profile">
            <span class="status-indicator-dot"></span>
          </div>
          <button type="button" class="modal-close-icon-btn" id="account-header-close-btn" aria-label="Close Profile" title="Close (Esc)">
            <svg class="dialog-close-svg" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="2.5" y1="2.5" x2="11.5" y2="11.5" />
              <line x1="11.5" y1="2.5" x2="2.5" y2="11.5" />
            </svg>
          </button>
        </div>
      </header>

      <div class="fullscreen-dialog-body">
        <div class="account-profile-single-panel fullscreen-card-panel" style="max-width: 520px; width: 100%; margin: 0 auto;">
          <!-- Avatar & Profile preview -->
          <div style="display: flex; align-items: center; gap: 18px; margin-bottom: 22px; padding-bottom: 20px; border-bottom: 1px solid rgba(255, 255, 255, 0.08);">
            <div style="width: 64px; height: 84px; border-radius: 8px; border: 1.5px solid rgba(0, 240, 255, 0.45); overflow: hidden; position: relative; background: #060911; flex-shrink: 0; box-shadow: 0 0 16px rgba(0, 240, 255, 0.3);">
              <img src="${avatarUrl}" alt="Commander Avatar" style="width: 100%; height: 100%; object-fit: cover; display: block;" onerror="this.onerror=null;this.src='assets/cards/1_Hypermarine.png';" />
            </div>
            <div style="flex: 1; min-width: 0;">
              <div style="display: flex; align-items: center; gap: 10px;">
                <span class="stat-value" id="account-dossier-name" style="font-family: 'Orbitron', sans-serif; font-size: 20px; font-weight: 800; color: #f8fafc; letter-spacing: 1px; text-shadow: 0 0 14px rgba(0, 240, 255, 0.35);">
                  ${escapeHtml(displayName)}
                </span>
                <button type="button" class="account-pen-btn" id="account-edit-name-btn" title="Edit Call-Sign">✎</button>
              </div>
              <div style="font-family: 'Rajdhani', sans-serif; font-size: 13px; font-weight: 700; letter-spacing: 1px; color: #94a3b8; margin-top: 5px;">
                OPERATIVE ID: <span style="font-family: 'IBM Plex Mono', monospace; color: #38bdf8; font-weight: 700;">${hasAccount ? `#${auth.accountId}` : 'GUEST-UNREGISTERED'}</span>
              </div>
            </div>
          </div>

          <div class="account-stat-list">
            <div class="account-stat-row">
              <span class="stat-label">COMBAT RATING</span>
              <span class="stat-value" style="color: #00f0ff; font-family: 'IBM Plex Mono', monospace; font-size: 15px; font-weight: 800; letter-spacing: 0.5px;">${mmr} MMR</span>
            </div>

            <div class="account-stat-row">
              <span class="stat-label">RANK TIER</span>
              <span class="stat-value" style="color: ${tier.color}; font-size: 15px; font-weight: 800; display: inline-flex; align-items: center; gap: 6px;">
                <span>${tier.icon}</span> <span>${tier.name}</span>
              </span>
            </div>

            <div class="account-stat-row">
              <span class="stat-label">SECURITY STATUS</span>
              <span class="stat-value" style="color: ${hasAccount ? '#22c55e' : '#fbbf24'}; font-size: 14.5px; font-weight: 800; letter-spacing: 0.8px;">${hasAccount ? 'AUTHENTICATED' : 'ANONYMOUS GUEST'}</span>
            </div>

            <div class="account-stat-row">
              <span class="stat-label">DATA PRIVACY</span>
              <span class="stat-value" style="color: #38bdf8; font-size: 14px; font-weight: 800; letter-spacing: 0.5px;">100% COOKIELESS · OCC</span>
            </div>
          </div>

          <div style="margin-top: 16px; width: 100%; display: flex; justify-content: center;">
            <button type="button" class="start-btn start-btn-secondary" id="account-open-recovery-btn" style="height: 48px; width: 100%;">
              <span class="start-btn-text">${hasAccount ? 'RESTORE ANOTHER ACCOUNT' : 'RESTORE EXISTING ACCOUNT'}</span>
            </button>
          </div>

          <!-- Data Feed Section -->
          <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.08); width: 100%;">
            <button type="button" class="start-btn start-btn-primary" id="account-open-feed-btn" style="width: 100%; height: 46px;">
              <span class="start-btn-text">DATA FEED // TRANSMISSIONS</span>
            </button>
          </div>

          <div style="margin-top: 16px; padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.08); width: 100%;">
          <!-- Clearance Voucher Section -->
          <div class="account-voucher-tray" style="margin-top: 0; padding: 12px 14px; background: rgba(0, 240, 255, 0.04); border: 1px solid rgba(0, 240, 255, 0.2); border-radius: 8px;">
            <div style="margin-bottom: 8px;">
              <span style="font-family: 'Orbitron', sans-serif; font-size: 11px; font-weight: 800; color: #00f0ff; letter-spacing: 1.4px; display: inline-flex; align-items: center; gap: 6px;">
                <span>⬡</span> PROMO &amp; EVENT CODES
              </span>
            </div>
            <div style="display: flex; gap: 8px;">
              <input type="text" id="account-voucher-input" placeholder="ENTER CODE" class="data-feed-voucher-input" maxlength="32" spellcheck="false" autocomplete="off" style="flex: 1; min-width: 0; max-width: none; height: 38px;" />
              <button type="button" id="account-voucher-btn" class="data-feed-voucher-btn" style="height: 38px; padding: 0 16px;">REDEEM</button>
            </div>
            <div id="account-voucher-status" style="display: none; margin-top: 8px; font-family: 'IBM Plex Mono', monospace; font-size: 11.5px; font-weight: 700; letter-spacing: 0.8px; text-align: center;"></div>
          </div>
          </div>
        </div>
      </div>

      <!-- Centered Bottom Action Button -->
      <footer class="fullscreen-dialog-footer">
        <button type="button" class="fullscreen-return-btn" id="account-return-btn">
          <span class="start-btn-text">RETURN</span>
        </button>
      </footer>
    </div>
  `;

  document.body.appendChild(layer);
  activeOverlay = layer;

  // Close handlers
  const closeModal = () => {
    audio?.playUiClick?.();
    closeRecoverPrompt();
    layer.remove();
    activeOverlay = null;
    game?.overlayGate?.release?.();
    window.removeEventListener('keydown', handleKey);
  };

  const returnBtn = layer.querySelector('#account-return-btn');
  returnBtn?.addEventListener('click', closeModal);
  const headerCloseBtn = layer.querySelector('#account-header-close-btn');
  headerCloseBtn?.addEventListener('click', closeModal);

  const feedBtn = layer.querySelector('#account-open-feed-btn');
  feedBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    closeModal();
    showDataFeedModal(game);
  });

  const handleKey = (e) => {
    if (e.key === 'Escape') closeModal();
  };
  window.addEventListener('keydown', handleKey);

  // Edit Call-Sign handler
  const editBtn = layer.querySelector('#account-edit-name-btn');
  editBtn?.addEventListener('click', async () => {
    audio?.playUiClick?.();
    const granted = await checkAlphaAccess({ prompt: true });
    if (!granted) return;

    showCallsignModal({
      game,
      onSaved: (newName) => {
        const updatedName = newName || getDisplayName() || 'Commander';
        const nameEl = layer.querySelector('#account-dossier-name');
        if (nameEl) nameEl.textContent = updatedName;
      }
    });
  });

  // Account Recovery handler
  const recoverBtn = layer.querySelector('#account-open-recovery-btn');
  recoverBtn?.addEventListener('click', () => {
    audio?.playUiClick?.();
    showRecoverPrompt();
  });

  // Clearance Voucher redemption handler
  const voucherInput = layer.querySelector('#account-voucher-input');
  const voucherBtn = layer.querySelector('#account-voucher-btn');
  const voucherStatus = layer.querySelector('#account-voucher-status');

  const handleRedeem = async () => {
    const code = voucherInput?.value?.trim();
    if (!code) return;

    voucherBtn.disabled = true;
    voucherBtn.textContent = 'VERIFYING...';
    if (voucherStatus) voucherStatus.style.display = 'none';

    const res = await inboxManager.redeemVoucher(code);
    voucherBtn.disabled = false;
    voucherBtn.textContent = 'REDEEM';

    if (res.ok) {
      audio?.playDailyJackpotFanfare?.();
      if (voucherInput) voucherInput.value = '';
      if (voucherStatus) {
        voucherStatus.style.display = 'block';
        voucherStatus.style.color = '#10b981';
        voucherStatus.textContent = `✓ ${res.title.toUpperCase()} REDEEMED`;
      }
      showRewardToast(res.rewards);
    } else {
      audio?.playUiClick?.();
      if (voucherStatus) {
        voucherStatus.style.display = 'block';
        voucherStatus.style.color = '#ef4444';
        voucherStatus.textContent = `✕ ${res.message.toUpperCase()}`;
      }
    }
  };

  voucherBtn?.addEventListener('click', handleRedeem);
  voucherInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRedeem();
    }
  });
}

function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
